import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

const STAGE_COLOR = {
  NEW: "bg-blue-100 text-blue-900", QUALIFIED: "bg-violet-100 text-violet-900",
  PROPOSAL: "bg-amber-100 text-amber-900", NEGOTIATION: "bg-orange-100 text-orange-900",
  WON: "bg-emerald-100 text-emerald-900", LOST: "bg-red-100 text-red-900", ARCHIVED: "bg-muted text-foreground",
};

const STAGES = ["NEW", "QUALIFIED", "PROPOSAL", "NEGOTIATION", "WON", "LOST", "ARCHIVED"];

export default function Leads() {
  const { user } = useAuth();
  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";
  const [tab, setTab] = useState("ALL");
  const [scope, setScope] = useState(isManager ? "team" : "mine");
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", source: "website", estimated_value: 0, region: "", customer_type: "BUILDER", assigned_to: "" });

  const load = async () => {
    const params = {};
    if (tab !== "ALL") params.stage = tab;
    if (scope === "mine") params.mine = true;
    const { data } = await api.get("/sales/leads", { params });
    setItems(data);
  };
  const loadTeam = async () => {
    try { const { data } = await api.get("/sales/team"); setTeam(data); }
    catch { setTeam([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, scope]);
  useEffect(() => { if (isManager) loadTeam(); /* eslint-disable-next-line */ }, [isManager]);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/sales/leads/${editing.id}`, form);
        toast.success("Lead updated");
      } else {
        await api.post("/sales/leads", form);
        toast.success("Lead created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-leads">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Leads</h1>
          <p className="text-muted-foreground">Pipeline from new lead → won deal. Assign, follow up, note and convert to quotation.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: "", company: "", phone: "", email: "", source: "website", estimated_value: 0, region: "", customer_type: "BUILDER", assigned_to: "" }); setOpen(true); }} data-testid="lead-create-btn"><Plus className="w-4 h-4 mr-1" /> New lead</Button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="ALL" data-testid="lead-tab-ALL">All</TabsTrigger>
            {STAGES.map((s) => <TabsTrigger key={s} value={s} data-testid={`lead-tab-${s}`}>{s}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        {isManager && (
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger value="team" data-testid="lead-scope-team">Team leads</TabsTrigger>
              <TabsTrigger value="mine" data-testid="lead-scope-mine">My leads</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Stage</TableHead>
              <TableHead>Value</TableHead><TableHead>Source</TableHead><TableHead>Assigned</TableHead>
              <TableHead>Next follow-up</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-semibold">{l.name}</TableCell>
                <TableCell className="text-sm">{l.company || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`${STAGE_COLOR[l.stage]} font-mono text-[10px]`}>{l.stage}</Badge></TableCell>
                <TableCell>₹{(l.estimated_value || 0).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{l.source || "—"}</TableCell>
                <TableCell className="text-xs">{team.find((t) => t.id === l.assigned_to)?.full_name || l.assigned_to?.slice(0, 8) || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{l.next_followup_at ? new Date(l.next_followup_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(l); setForm({ ...l, assigned_to: l.assigned_to || "" }); setOpen(true); }} data-testid={`lead-edit-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No leads yet — create one to start the pipeline.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit lead" : "New lead"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
            <div><Label>Company</Label><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company" /></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Estimated value (₹)</Label><Input type="number" value={form.estimated_value || 0} onChange={(e) => setForm({ ...form, estimated_value: parseFloat(e.target.value || "0") })} data-testid="lead-value" /></div>
            <div><Label>Source</Label><Input value={form.source || ""} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            <div>
              <Label>Customer type</Label>
              <Select value={form.customer_type || "BUILDER"} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["BUILDER","ARCHITECT","DESIGNER","CONTRACTOR","CUSTOMER"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && (
              <div>
                <Label>Stage</Label>
                <Select value={form.stage || "NEW"} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="lead-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {isManager && (
              <div>
                <Label>Assigned to</Label>
                <Select value={form.assigned_to || ""} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger data-testid="lead-assigned"><SelectValue placeholder="Pick executive…" /></SelectTrigger>
                  <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name || t.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {editing && <div className="col-span-2"><Label>Add note</Label><Input value={form.notes_add || ""} onChange={(e) => setForm({ ...form, notes_add: e.target.value })} placeholder="Quick note…" data-testid="lead-note" /></div>}
            {editing && <div><Label>Next follow-up</Label><Input type="datetime-local" value={(form.next_followup_at || "").slice(0,16)} onChange={(e) => setForm({ ...form, next_followup_at: e.target.value })} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="lead-save">{editing ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
